/**
 * Foto's verkleinen voordat ze verstuurd worden.
 *
 * Een telefoonfoto is al snel 3 tot 5 MB. Voor de vraag of er tegels liggen of
 * waar de regenpijp zit is 1600 pixels ruim genoeg. Dat scheelt opslagruimte,
 * maar vooral tijd: een coach staat met 4G in een achtertuin te wachten tot de
 * upload klaar is.
 *
 * Belangrijk uitgangspunt: verkleinen mag nooit een upload tegenhouden. Lukt
 * het niet — een onbekend formaat, een browser die niet meewerkt — dan gaat het
 * origineel gewoon mee.
 */

export interface VerkleinOpties {
  /** Langste zijde in pixels. */
  maxZijde?: number;
  /** JPEG-kwaliteit tussen 0 en 1. */
  kwaliteit?: number;
}

const MAX_ZIJDE = 1600;
const KWALITEIT = 0.82;

/** Onder deze grootte is verkleinen de moeite niet meer waard. */
const AL_KLEIN_GENOEG_BYTES = 600_000;

/**
 * Laadt het bestand als iets waar we op een canvas mee kunnen tekenen.
 *
 * `createImageBitmap` met `imageOrientation` draait de foto meteen goed:
 * telefoons slaan een staande foto vaak liggend op met een draai-instructie
 * erin. Kent de browser die optie niet, dan doet een gewoon <img>-element
 * dat tegenwoordig ook zelf.
 */
async function laadBron(
  bestand: File
): Promise<{ bron: CanvasImageSource; breedte: number; hoogte: number; opruimen: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(bestand, { imageOrientation: 'from-image' });
      return {
        bron: bitmap,
        breedte: bitmap.width,
        hoogte: bitmap.height,
        opruimen: () => bitmap.close(),
      };
    } catch {
      // Oudere browser of niet-ondersteund formaat: hieronder de terugval.
    }
  }

  const objectUrl = URL.createObjectURL(bestand);
  const afbeelding = new Image();

  try {
    await new Promise<void>((resolve, reject) => {
      afbeelding.onload = () => resolve();
      afbeelding.onerror = () => reject(new Error('Afbeelding kon niet worden gelezen.'));
      afbeelding.src = objectUrl;
    });
  } catch (fout) {
    URL.revokeObjectURL(objectUrl);
    throw fout;
  }

  return {
    bron: afbeelding,
    breedte: afbeelding.naturalWidth,
    hoogte: afbeelding.naturalHeight,
    opruimen: () => URL.revokeObjectURL(objectUrl),
  };
}

/** Vervangt de extensie door .jpg, want we schrijven altijd JPEG weg. */
function jpgNaam(naam: string): string {
  const punt = naam.lastIndexOf('.');
  const stam = punt > 0 ? naam.slice(0, punt) : naam;
  return `${stam || 'foto'}.jpg`;
}

/**
 * Verkleint een afbeelding. Geeft het origineel terug als verkleinen niet
 * lukt, niet nodig is, of niets zou opleveren.
 */
export async function verkleinAfbeelding(
  bestand: File,
  opties: VerkleinOpties = {}
): Promise<File> {
  const maxZijde = opties.maxZijde ?? MAX_ZIJDE;
  const kwaliteit = opties.kwaliteit ?? KWALITEIT;

  if (!bestand.type.startsWith('image/')) return bestand;

  let opruimen: (() => void) | null = null;

  try {
    const geladen = await laadBron(bestand);
    opruimen = geladen.opruimen;

    const { bron, breedte, hoogte } = geladen;
    if (!breedte || !hoogte) return bestand;

    const schaal = Math.min(1, maxZijde / Math.max(breedte, hoogte));

    // Al klein van formaat en van bestandsgrootte: laat hem met rust.
    if (schaal === 1 && bestand.size <= AL_KLEIN_GENOEG_BYTES) return bestand;

    const doelBreedte = Math.max(1, Math.round(breedte * schaal));
    const doelHoogte = Math.max(1, Math.round(hoogte * schaal));

    const canvas = document.createElement('canvas');
    canvas.width = doelBreedte;
    canvas.height = doelHoogte;

    const context = canvas.getContext('2d');
    if (!context) return bestand;

    context.imageSmoothingQuality = 'high';
    context.drawImage(bron, 0, 0, doelBreedte, doelHoogte);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', kwaliteit);
    });

    // Levert het niets op, dan is het origineel beter (bijv. bij een kleine PNG).
    if (!blob || blob.size >= bestand.size) return bestand;

    return new File([blob], jpgNaam(bestand.name), {
      type: 'image/jpeg',
      lastModified: bestand.lastModified,
    });
  } catch {
    // Verkleinen is een verbetering, geen voorwaarde.
    return bestand;
  } finally {
    opruimen?.();
  }
}
